import { Typography } from '@mui/material';
import { Mail, PersonOutline, PhoneAndroid, Title } from '@mui/icons-material';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { postContactUs } from '../../../api/contactus.api';
import { cellPhonePattern, emailPattern } from '../../../utils/forms.utils';
import Alert from '../../foundation/Alert';
import Button from '../../foundation/Button';
import CustomBR from '../../foundation/CustomBR';
import DialogWithHeader from '../../foundation/DialogWithHeader';
import TextArea from '../../foundation/TextArea';
import TextField from '../../foundation/TextField';

export type ElpContactModalProps = {
    open: boolean
    onClose: () => void
}

type ContactForm = {
    fullname: string
    phone: string
    email: string
    company: string
    message: string
}

const ElpContactModal = ({ open, onClose }: ElpContactModalProps): JSX.Element => {
    const [success, setSuccess] = useState(false);
    const [formErr, setFormErr] = useState<string>(null);
    const {
        register, handleSubmit, formState: { errors }, reset,
    } = useForm<ContactForm>();

    const onSubmit = async (data: ContactForm) => {
        try {
            window.showBackdrop();
            await postContactUs({
                fullname: data.fullname,
                email: data.email,
                phone: data.phone,
                subject: data.company ? `פנייה מדף מעסיקים - ${data.company}` : 'פנייה מדף מעסיקים',
                message: data.message || `פנייה מדף /elp${data.company ? `\nשם חברה: ${data.company}` : ''}`,
            });
            window.hideBackdrop();
            setSuccess(true);
            reset();
        } catch (error) {
            window.hideBackdrop();
            setFormErr(error.message);
        }
    };

    const handleClose = () => {
        setSuccess(false);
        setFormErr(null);
        onClose();
    };

    const fullnameInput = register('fullname', { required: true });
    const phoneInput = register('phone', { required: true, pattern: cellPhonePattern });
    const emailInput = register('email', { required: true, pattern: emailPattern });
    const companyInput = register('company');
    const messageInput = register('message');

    return (
        <DialogWithHeader open={open} onClose={handleClose} title="צרו איתי קשר" fullWidth maxWidth="sm">
            <Typography color="secondary" variant="body1" textAlign="center">
                נשמח לעזור לכם ולמצוא את העובדים המתאימים ביותר
            </Typography>
            <CustomBR />

            {success ? (
                <Alert severity="success">ההודעה נשלחה בהצלחה! נחזור אליך במהרה.</Alert>
            ) : (
                <>
                    {formErr && <><Alert severity="error">{formErr}</Alert><CustomBR /></>}
                    <form onSubmit={handleSubmit(onSubmit)}>
                        <TextField
                            required
                            fullWidth
                            label="שם מלא"
                            {...fullnameInput}
                            inputRef={fullnameInput.ref}
                            error={!!errors.fullname}
                            helperText={errors.fullname && 'חובה להזין שם'}
                            InputProps={{ endAdornment: <PersonOutline /> }}
                        />
                        <CustomBR />
                        <TextField
                            required
                            fullWidth
                            label="טלפון"
                            type="tel"
                            {...phoneInput}
                            inputRef={phoneInput.ref}
                            error={!!errors.phone}
                            helperText={errors.phone && 'מספר הטלפון אינו תקין'}
                            InputProps={{ endAdornment: <PhoneAndroid /> }}
                        />
                        <CustomBR />
                        <TextField
                            required
                            fullWidth
                            label="אימייל"
                            type="email"
                            {...emailInput}
                            inputRef={emailInput.ref}
                            error={!!errors.email}
                            helperText={errors.email && 'כתובת המייל אינה תקינה'}
                            InputProps={{ endAdornment: <Mail /> }}
                        />
                        <CustomBR />
                        <TextField
                            fullWidth
                            label="שם החברה"
                            {...companyInput}
                            inputRef={companyInput.ref}
                            InputProps={{ endAdornment: <Title /> }}
                        />
                        <CustomBR />
                        <TextArea
                            fullWidth
                            rows={3}
                            label="תוכן הפנייה (אופציונלי)"
                            {...messageInput}
                            inputRef={messageInput.ref}
                        />
                        <CustomBR />
                        <Button type="submit" color="primary" fullWidth size="large">
                            חזרו אליי
                        </Button>
                    </form>
                </>
            )}
        </DialogWithHeader>
    );
};

export default ElpContactModal;
